import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  CloudRain,
  CloudLightning,
  Cloud,
  ArrowClockwise,
} from "@phosphor-icons/react";
import LocationBar from "@/components/v2/LocationBar";
import RainConfidenceBar from "@/components/v2/RainConfidenceBar";
import WeatherDecisionCard from "@/components/v2/WeatherDecisionCard";
import LightningAlertCard from "@/components/v2/LightningAlertCard";
import SeasonChip from "@/components/v2/SeasonChip";
import RainModePill from "@/components/v2/RainModePill";
import AtmosphereStrip from "@/components/v2/AtmosphereStrip";
import VitalSignsRow from "@/components/v2/VitalSignsRow";
import SixHourTimeline from "@/components/v2/SixHourTimeline";
import ModelConfidenceChip from "@/components/v2/ModelConfidenceChip";
import { useVerdict } from "@/components/v2/useVerdict";
import { friendlyExplanation } from "@/lib/friendlyVerdict";
import useConfidenceTrend from "@/hooks/useConfidenceTrend";
import useNowcastTimeline from "@/hooks/useNowcastTimeline";
import NowcastNext30Min from "@/components/v2/NowcastNext30Min";
import { getThemeById } from "@/lib/weatherTheme";
import { useSettings } from "@/lib/settings";
import { getAirQuality } from "@/lib/api";

const NOW_BADGE = {
  rain: { Icon: CloudRain, label: "Raining now" },
  storm: { Icon: CloudLightning, label: "Storm now" },
  cloudy: { Icon: Cloud, label: "Cloudy now" },
};

/**
 * VerdictPage — Phase 60.4. Now the *complete* home page: confidence
 * verdict + everything that used to live on the Today tab (atmosphere
 * chips, vital signs, hourly strip with model-confidence chip, optional
 * marine card). The middle section scrolls vertically when content
 * overflows the viewport so the LocationBar (top) and pager (bottom)
 * stay anchored.
 *
 * The 7-month seasonal outlook now lives on TodayPage instead.
 */
export default function VerdictPage({
  location,
  onSelectLocation,
  forecast,
  confidence,
  summary,
  theme,
  status,
  onRetry,
}) {
  const [loadStuck, setLoadStuck] = useState(false);
  const { settings } = useSettings();
  const nowcastNext = useNowcastTimeline(location);
  const [aqi, setAqi] = useState(null);

  useEffect(() => {
    if (!location?.latitude || !location?.longitude) return undefined;
    let cancelled = false;
    setAqi(null);
    getAirQuality(location.latitude, location.longitude)
      .then((r) => !cancelled && setAqi(r))
      .catch(() => {});
    return () => { cancelled = true; };
  }, [location?.latitude, location?.longitude]);

  useEffect(() => {
    if (forecast) {
      setLoadStuck(false);
      return undefined;
    }
    const t = setTimeout(() => setLoadStuck(true), 12000);
    return () => clearTimeout(t);
  }, [forecast, location?.latitude, location?.longitude]);

  const showLoadError = !forecast && (status === "offline" || loadStuck);

  const verdict = useVerdict(forecast);
  const cur = forecast?.current || {};
  const trend = useConfidenceTrend(
    confidence?.score ?? null,
    location?.latitude,
    location?.longitude,
  );

  const sceneTheme = verdict.mood ? getThemeById(verdict.mood) : theme;
  const scene = { ...sceneTheme, accent: theme.accent };

  const curState = (() => {
    const cp = cur.precipitation || 0;
    const code = cur.weather_code;
    if (code >= 95) return "storm";
    if (cp >= 0.2 || (code >= 51 && code <= 82)) return "rain";
    if (code === 3 || (code >= 45 && code <= 48)) return "cloudy";
    return null;
  })();
  const nowBadge =
    curState && verdict.mood && curState !== verdict.mood
      ? NOW_BADGE[curState]
      : null;

  return (
    <div
      className="h-full w-full flex flex-col px-5 pt-2 pb-24"
      data-testid="page-verdict"
      style={{ color: theme.textPrimary }}
    >
      {/* Top — location pill (anchored, never scrolls) */}
      <div className="shrink-0">
        <LocationBar
          location={location}
          onSelect={onSelectLocation}
          theme={theme}
        />
      </div>

      {showLoadError ? (
        <div
          className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-3 min-h-0"
          data-testid="forecast-load-error"
        >
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center"
            style={{ background: theme.chip, border: `1px solid ${theme.surfaceBorder}` }}
          >
            <CloudRain size={28} weight="duotone" style={{ color: theme.accent }} />
          </div>
          <div>
            <p className="text-[15px] font-semibold leading-tight" style={{ color: theme.textPrimary }}>
              Couldn't load the forecast
            </p>
            <p className="text-[12px] leading-relaxed mt-1.5 max-w-[280px] mx-auto" style={{ color: theme.textMuted }}>
              Our weather service is busy right now. This usually clears within a
              minute — tap retry to try again.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setLoadStuck(false);
              if (onRetry) onRetry();
            }}
            data-testid="forecast-retry-button"
            className="mt-1 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-semibold transition-transform active:scale-95"
            style={{
              background: theme.accent,
              color: "#fff",
              boxShadow: `0 4px 14px ${theme.accent}40`,
            }}
          >
            <ArrowClockwise size={13} weight="bold" />
            Retry
          </button>
        </div>
      ) : (
        // Scrollable middle column. min-h-0 + overflow-y-auto lets the
        // LocationBar / pager stay anchored while this region scrolls.
        <div
          className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1 mt-2 pb-1"
          style={{ scrollbarWidth: "none" }}
        >
          {/* HERO — confidence + verdict prose */}
          <div className="flex flex-col items-center text-center gap-2.5">
            <SeasonChip location={location} theme={theme} />
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
              className="relative w-full"
            >
              <RainConfidenceBar
                confidence={confidence}
                theme={theme}
                sceneTheme={scene}
                trend={trend}
              />
              {nowBadge && (
                <motion.div
                  className="absolute -top-2 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full"
                  style={{
                    background: theme.surface,
                    border: `1px solid ${theme.surfaceBorder}`,
                    color: theme.textPrimary,
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(10px)",
                  }}
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.4 }}
                  data-testid="now-badge"
                >
                  <nowBadge.Icon size={11} weight="duotone" style={{ color: theme.accent }} />
                  <span className="text-[10px] font-semibold uppercase tracking-wider">
                    {nowBadge.label}
                  </span>
                </motion.div>
              )}
            </motion.div>

            {nowcastNext && <NowcastNext30Min data={nowcastNext} theme={theme} />}

            {/* Headline — single-line preferred. Wider max-width + slightly
                smaller weight so common verdicts ("Storm watch — models show
                no rain yet") fit on 1 line on most phones, max 2 on narrow.
                Phase 60.8 — utilise horizontal space, don't break onto 5+
                lines. */}
            <motion.h1
              className="font-display text-[18px] sm:text-[20px] font-semibold tracking-tight leading-[1.25] max-w-[42ch] text-balance px-2 mt-1"
              style={{ color: theme.textPrimary }}
              data-testid="verdict-headline"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.18 }}
            >
              {confidence?.headline && settings.language === "en"
                ? confidence.headline
                : summary?.headline || verdict.headline}
            </motion.h1>

            {/* Detail — wider container, no hyphenation. Most plain-English
                explanations fit in 2 lines instead of breaking onto 4-5. */}
            <motion.p
              className="text-[12.5px] leading-[1.5] max-w-[44ch] px-2 text-pretty"
              style={{
                color: theme.textSecondary,
                textAlign: "center",
                hyphens: "manual",
                WebkitHyphens: "manual",
              }}
              data-testid="verdict-sub"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.26 }}
            >
              {friendlyExplanation(confidence) ||
                summary?.detail ||
                verdict.sub}
            </motion.p>

            {confidence?.motion && confidence.motion.kind === "incoming" && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.42 }}
                data-testid="verdict-mode-row"
              >
                <RainModePill mode={confidence.mode} motion={confidence.motion} />
              </motion.div>
            )}
          </div>

          {/* LIGHTNING ALERT — auto-show when strikes within 15 km */}
          <LightningAlertCard location={location} theme={theme} />

          {/* RAIN DECISION — the killer "what should I do?" tile */}
          <div className="mt-3">
            <WeatherDecisionCard forecast={forecast} nowcast={nowcastNext} aqi={aqi} theme={theme} />
          </div>

          {/* ATMOSPHERE — chips */}
          <SectionLabel theme={theme}>Atmosphere · right now</SectionLabel>
          <AtmosphereStrip forecast={forecast} theme={theme} />

          {/* VITAL SIGNS */}
          <SectionLabel theme={theme}>Vital signs</SectionLabel>
          <VitalSignsRow forecast={forecast} theme={theme} />

          {/* NEXT 6 HOURS + model-confidence chip */}
          <div className="flex items-center justify-between mt-4 mb-1.5">
            <span
              className="text-[10px] font-bold uppercase tracking-[0.2em]"
              style={{ color: theme.textMuted }}
            >
              Next 6 hours
            </span>
            <ModelConfidenceChip hourly={forecast?.hourly} theme={theme} />
          </div>
          <SixHourTimeline hourly={forecast?.hourly} theme={theme} />
        </div>
      )}
    </div>
  );
}

function SectionLabel({ theme, children }) {
  return (
    <div
      className="text-[10px] font-bold uppercase tracking-[0.2em] mt-4 mb-1.5"
      style={{ color: theme.textMuted }}
    >
      {children}
    </div>
  );
}
